import React, { useEffect, useState } from 'react';
import { Card, Collapse, Tag, Input, Select, Spin, Empty, Space, Button, Modal, Table, message as antMessage } from 'antd';
import { PhoneOutlined, SearchOutlined } from '@ant-design/icons';
import { numberApi, smsApi } from '../../services/api';
import type { NumberStatus, SMS } from '../../types';
import dayjs from 'dayjs';

const { Panel } = Collapse;
const { Search } = Input;

const Numbers: React.FC = () => {
  const [numbers, setNumbers] = useState<NumberStatus[]>([]);
  const [groupedData, setGroupedData] = useState<Record<string, NumberStatus[]>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'country' | 'batch'>('country');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'in_use'>('all');

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<string>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [messages, setMessages] = useState<SMS[]>([]);

  useEffect(() => {
    loadNumbers();
  }, [viewMode]);

  const loadNumbers = async () => {
    setLoading(true);
    try {
      if (viewMode === 'country') {
        const data = await numberApi.getByCountry();
        setGroupedData(data);
      } else {
        const data = await numberApi.getByBatch();
        // Transform batch data to match the structure
        const transformed: Record<string, NumberStatus[]> = {};
        Object.entries(data).forEach(([batchName, batchInfo]: [string, any]) => {
          transformed[batchName] = batchInfo.numbers || [];
        });
        setGroupedData(transformed);
      }
    } catch (error) {
      console.error('Failed to load numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = Object.entries(groupedData).reduce((acc, [key, numbers]) => {
    const filtered = numbers.filter(n => {
      const matchesSearch = n.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || n.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    if (filtered.length > 0) {
      acc[key] = filtered;
    }
    return acc;
  }, {} as Record<string, NumberStatus[]>);

  // Flatten data for pagination
  const allFilteredNumbers = Object.entries(filteredData).flatMap(([key, nums]) =>
    nums.map(n => ({ ...n, groupKey: key }))
  );

  const totalNumbers = allFilteredNumbers.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedNumbers = allFilteredNumbers.slice(startIndex, endIndex);

  // Re-group paginated data
  const paginatedGroupedData = paginatedNumbers.reduce((acc, num) => {
    const key = num.groupKey;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(num);
    return acc;
  }, {} as Record<string, (NumberStatus & { groupKey: string })[]>);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleNumberClick = async (phoneNumber: string) => {
    console.log('Number clicked:', phoneNumber);
    setSelectedNumber(phoneNumber);
    setModalVisible(true);
    setModalLoading(true);
    setMessages([]);

    try {
      console.log('Fetching messages for:', phoneNumber);
      const data = await smsApi.search(phoneNumber);
      console.log('Messages received:', data?.length || 0);
      setMessages(data || []);
    } catch (error) {
      antMessage.error('Failed to load messages for this number');
      console.error('Failed to load messages:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedNumber('');
    setMessages([]);
  };

  const renderNumber = (number: NumberStatus) => {
    const isAvailable = number.status === 'available';
    return (
      <div
        key={number.phoneNumber}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 12px',
          margin: '4px 0',
          background: '#f5f5f5',
          borderRadius: 4,
          borderLeft: `4px solid ${isAvailable ? '#52c41a' : '#ff4d4f'}`,
          cursor: 'pointer',
          transition: 'all 0.3s',
        }}
        onClick={() => handleNumberClick(number.phoneNumber)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#e6f7ff';
          e.currentTarget.style.transform = 'translateX(4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#f5f5f5';
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        <span>
          <PhoneOutlined style={{ marginRight: 8 }} />
          {number.phoneNumber}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {number.messageCount > 0 && (
            <span style={{ fontSize: 12, color: '#666' }}>
              {number.messageCount} {number.messageCount === 1 ? 'message' : 'messages'}
            </span>
          )}
          <Tag color={isAvailable ? 'green' : 'red'}>
            {isAvailable ? 'Available' : 'In Use'}
          </Tag>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Table columns for modal
  const columns = [
    {
      title: 'Date/Time',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 180,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: SMS, b: SMS) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 150,
      ellipsis: true,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (text: string) => (
        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text || '-'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
        <h1>Phone Numbers</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
          >
            <Select.Option value="all">All Status</Select.Option>
            <Select.Option value="available">Available</Select.Option>
            <Select.Option value="in_use">In Use</Select.Option>
          </Select>
          <Select
            value={viewMode}
            onChange={setViewMode}
            style={{ width: 150 }}
          >
            <Select.Option value="country">By Country</Select.Option>
            <Select.Option value="batch">By Batch</Select.Option>
          </Select>
          <Search
            placeholder="Search phone number"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 32 }}>
          <div>
            <strong>Total Numbers:</strong> {totalNumbers}
          </div>
          <div>
            <Tag color="green">Available</Tag>
            <span>
              {Object.values(filteredData)
                .flat()
                .filter(n => n.status === 'available').length}
            </span>
          </div>
          <div>
            <Tag color="red">In Use</Tag>
            <span>
              {Object.values(filteredData)
                .flat()
                .filter(n => n.status === 'in_use').length}
            </span>
          </div>
        </div>
      </Card>

      {Object.keys(paginatedGroupedData).length === 0 ? (
        <Empty description="No numbers found" />
      ) : (
        <>
          <Collapse defaultActiveKey={Object.keys(paginatedGroupedData)}>
            {Object.entries(paginatedGroupedData).map(([key, numbers]) => {
            const available = numbers.filter(n => n.status === 'available').length;
            const inUse = numbers.filter(n => n.status === 'in_use').length;

            return (
              <Panel
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>{key}</strong>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <span>{numbers.length} numbers</span>
                      <Tag color="green">{available} available</Tag>
                      <Tag color="red">{inUse} in use</Tag>
                    </div>
                  </div>
                }
                key={key}
              >
                {numbers.map(renderNumber)}
              </Panel>
            );
          })}
          </Collapse>
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Card size="small">
              <Space>
                <span>
                  Showing {startIndex + 1}-{Math.min(endIndex, totalNumbers)} of {totalNumbers} numbers
                </span>
                <Select
                  value={pageSize}
                  onChange={(value) => handlePageChange(1, value)}
                  style={{ width: 120 }}
                >
                  <Select.Option value={20}>20 / page</Select.Option>
                  <Select.Option value={50}>50 / page</Select.Option>
                  <Select.Option value={100}>100 / page</Select.Option>
                  <Select.Option value={200}>200 / page</Select.Option>
                </Select>
                <Button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                <span>Page {currentPage} of {Math.ceil(totalNumbers / pageSize)}</span>
                <Button
                  disabled={currentPage >= Math.ceil(totalNumbers / pageSize)}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </Space>
            </Card>
          </div>
        </>
      )}

      {/* Messages Modal */}
      <Modal
        title={`Messages for ${selectedNumber}`}
        open={modalVisible}
        onCancel={handleModalClose}
        width={900}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={columns}
          dataSource={messages}
          loading={modalLoading}
          rowKey="id"
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} messages`,
          }}
          scroll={{ x: true }}
          locale={{
            emptyText: modalLoading ? 'Loading...' : 'No messages found for this number',
          }}
        />
      </Modal>
    </div>
  );
};

export default Numbers;
